// ArchetypeTerm.tsx (CX9) - the tap-to-learn trigger for a player TYPE, the archetype twin of <Term>. Where
// <Term term="ladder"> opens a glossary entry, <ArchetypeTerm id="surgeon"> opens that archetype's explainer
// (what the type is + what it means for you) in the SAME locked bottom-sheet, via openContent. One trigger,
// wired into every surface that shows an archetype name - the pro's note, the showdown reveal, the read UI -
// so "the Surgeon" opens the identical bubble wherever you tap it.
//
// Two looks over one behaviour:
//   - default: an inline dashed-underline term, styled exactly like <Term> (`.term`), for a name that flows
//     inside prose ("the Surgeon raises" in the pro's bubble).
//   - chip: a standalone tappable pill (`.term-chip`) for a name that stands on its own (a showdown row).
import type { ReactNode } from 'react';
import type { ArchetypeId } from '@postflop/schema';
import { useGlossary } from './Glossary';
import { displayName } from '../engine/liveRunner';
import { archetypeExplainer } from '../data/archetypeExplainer';

export interface ArchetypeTermProps {
  id: ArchetypeId;
  children?: ReactNode;
  /** 'inline' (default) flows inside prose like <Term>; 'chip' is a standalone pill; 'dot' is a small "i"
   *  info affordance to sit beside a control that already has its own primary action (the read-UI tags). */
  variant?: 'inline' | 'chip' | 'dot';
}

const CLASS: Record<NonNullable<ArchetypeTermProps['variant']>, string> = {
  inline: 'term',
  chip: 'term-chip',
  dot: 'term-dot',
};

export function ArchetypeTerm({ id, children, variant = 'inline' }: ArchetypeTermProps) {
  const { openContent } = useGlossary();
  return (
    <button
      type="button"
      className={CLASS[variant]}
      onClick={() => openContent(archetypeExplainer(id))}
      aria-label={`What is ${displayName(id)}`}
    >
      {variant === 'dot' ? 'i' : children}
    </button>
  );
}
